// The monomorphization pass: lower a type-checked program to Core.
//
// Unlike the `Elaboration` (decisions over the *abstract* AST), this pass resolves
// types and dispatch in each *concrete* context — substituting `Self` (and, later,
// generic type parameters) — so a trait's default body becomes one specialized
// `CoreFunc` per implementing type, with `self` a concrete type and every call
// resolved to a `staticCall` or `dynCall`. That per-instantiation resolution is
// exactly what a compiled backend needs and what the abstract IR can't give (see
// the note in core.ts). Operator overloads and inherited defaults are lowered
// away, so Core carries neither.
//
// Scope: the whole language except tables — functions, structs / value types /
// enums, trait & impl methods (static and dynamic dispatch), operators, control
// flow, `match`, `Set`/`Dict` and iteration, generics (functions, structs, and
// methods, all monomorphized per instantiation), and first-class functions /
// closures.

import * as ast from "../frontend/ast.js";
import { builtinContainerInherentImpls, builtinEnums, builtinFns, builtinImpls, builtinStructs, builtinTraits, BUILTINS, containerTypeParams, preludeFns } from "../frontend/stdlib.js";
import { BOOL, CHAR, FLOAT, INT, STRING, Type, UNKNOWN, VOID } from "../frontend/types.js";
import {
  CoreArm,
  CoreDispatch,
  CoreEnum,
  CoreExpr,
  CoreFieldInit,
  CoreFunc,
  CoreIter,
  CoreMemoryMode,
  CoreProgram,
  CoreStmt,
  CoreStruct,
  CoreTxnStrategy,
  FuncId,
} from "./core.js";
import { Elaboration, emptyElaboration } from "./elaboration.js";
import { LockPlan } from "./lockplan.js";

/** Lower a type-checked program to Core. `plan` is the LockPlan whose decisions
 *  (memory mode + per-txn strategy) are baked into the resulting Core, so a
 *  backend reads them off Core rather than re-running the analysis. When omitted,
 *  Core defaults to mvcc with every txn optimistic — a safe, always-correct plan
 *  (matching `stm go`'s default "occ" strategy). `elab` is the checker's node-type
 *  IR; the pass reads it where a type needs the checker's resolution rather than its
 *  own local walk — notably a `Set<T>()`/`Dict<K, V>()` construction, whose element
 *  type the checker resolves and records on the node. */
export function monomorphize(program: ast.Program, plan?: LockPlan, elab?: Elaboration): CoreProgram {
  return new Monomorphizer(program, plan, elab ?? emptyElaboration()).run();
}

class MonoError extends Error {}

/** Operator -> the trait/method an overload dispatches to (mirrors the checker's
 *  OPERATOR_TRAITS and the interpreter). Comparisons map to Eq/Ord specially. */
const ARITH_METHOD: Record<string, string> = { "+": "add", "-": "sub", "*": "mul", "/": "div" };

/** The names the prelude's primitive `impl`s are keyed under (`impl str`/`impl char`
 *  and the scalar `impl Ord for …` blocks). A method on one of these is lowered on
 *  demand at its call site (see `lowerMethodCall`), not eagerly in `run`. `string`
 *  is written under the `str` alias. */
const PRIMITIVE_IMPL_KEYS = new Set(["str", "char", "int", "float", "bool"]);

/** The `impl` key for a primitive type — its `k`, except `string` -> `str` (the
 *  alias its impls use). Null for anything that isn't a primitive. */
function primitiveImplKey(t: Type): string | null {
  if (t.k === "string") return "str";
  if (t.k === "char" || t.k === "int" || t.k === "float" || t.k === "bool") return t.k;
  return null;
}

interface Method {
  name: string;
  params: ast.Param[];
  returnType: ast.TypeExpr | null;
  body: ast.Block;
  /** The method's own type parameters (`fn map<U>(…)`) — a generic *method*, not yet
   *  lowered (it needs per-call instantiation like a generic function). */
  typeParams: string[];
  /** A `builtin` method: no body, implemented natively. A call to it lowers to a
   *  `builtin` Core node (named `Type_method`) rather than a `staticCall`, so it is
   *  never lowered as a function — the backend plugs it in by name, like a builtin
   *  free function (see `lowerMethodCall`). */
  builtin?: boolean;
  /** Set when this is an *inherited trait default* (not written in the impl). The
   *  default's body is generic over the trait's type parameters, bound here to the
   *  implementing type's trait arguments (`Iterable<int>` for `Countdown` ⇒ `T=int`),
   *  which the per-call instantiation needs — the struct's own parameters aren't in
   *  scope in a trait default. */
  inheritedFrom?: { trait: string; traitArgs: ast.TypeExpr[] };
}

/** A lexical type environment: local name -> its concrete `Type`. */
class Env {
  private vars = new Map<string, Type>();
  constructor(private parent: Env | null = null) {}
  child(): Env {
    return new Env(this);
  }
  declare(name: string, t: Type) {
    this.vars.set(name, t);
  }
  lookup(name: string): Type | undefined {
    return this.vars.get(name) ?? this.parent?.lookup(name);
  }
}

class Monomorphizer {
  private structs = new Map<string, ast.StructDecl>();
  private valueTypes = new Set<string>();
  private enums = new Map<string, ast.EnumDecl>();
  private variantOf = new Map<string, { enumName: string; variant: ast.EnumVariant }>();
  private fns = new Map<string, ast.FnDecl>();
  private traits = new Map<string, ast.TraitDecl>();
  /** target type -> method name -> method (impl methods + materialized defaults). */
  private methodImpls = new Map<string, Map<string, Method>>();
  /** container name -> STM-bodied derived method (`Set.union`/…) from the inherent
   *  impl. Kept apart from `methodImpls` so these aren't eagerly lowered for every
   *  program (they'd pull an erased `Set__unknown` cursor even when unused); instead
   *  a call site lowers the shared, element-erased stub on demand (see
   *  `lowerMethodCall`) — the same laziness as a primitive impl method. */
  private containerDerived = new Map<string, Map<string, Method>>();
  /** trait -> implementing type names. */
  private traitImpls = new Map<string, string[]>();
  /** target type -> loop element type, from an Iterable/Iterator impl's trait arg. */
  private iterElem = new Map<string, Type>();
  /** Built-in collection cursors to emit natively (see `containerAdapter`). */
  private iterAdapters: import("./core.js").IterAdapter[] = [];
  /** `trait#method` pairs reached by a `dynCall` — the dispatch tables to build. */
  private dynDispatched = new Set<string>();

  /** Whether the stdlib `Range`/`RangeIter` are unshadowed — set in the constructor.
   *  Gates the `for`-over-`Range` → counted-loop lowering (`lowerRangeFor`). */
  private stdlibRangeActive = false;

  /** The concrete type `Self` resolves to in the body currently being lowered. */
  private self: Type | null = null;
  /** Generic type parameters bound to concrete types in the instantiation being
   *  lowered (`T` -> `Set<int>`). A type variable resolves through this. */
  private typeBindings = new Map<string, Type>();
  /** The return type of the function currently being lowered — the expected type
   *  for a `return <lambda>` (so the returned closure's parameters get pinned). */
  private currentRet: Type | null = null;
  /** Memo of generic-function instantiations by mangled id, so `bigger<Set>` is
   *  lowered once however many times it's called. */
  private fnInstances = new Map<FuncId, { id: FuncId; ret: Type }>();
  /** Concrete instantiations of generic structs by mangled name (`Pair__int_string`),
   *  materialized on demand. Emitted in place of the abstract templates. */
  private structInstances = new Map<string, CoreStruct>();
  /** Concrete instantiations of generic enums by mangled name (`Option__int`,
   *  `Res__IOError_string`), materialized on demand — the enum analogue of
   *  `structInstances`. Each variant's payload fields are resolved to concrete
   *  types, so a `match` reads a real type instead of an erased `any`. */
  private enumInstances = new Map<string, CoreEnum>();
  /** Built-in `fn` declarations by name — the source of a builtin call's declared
   *  return type, resolved (and so monomorphized) at each call site. */
  private builtinDecls = new Map(builtinFns().map((f) => [f.name, f] as const));
  /** A struct instance's origin (head name + type arguments) — flattening to a
   *  mangled name loses the args, so keep them for unification (`Pair<A,B>` against
   *  a `Pair__int_string` receiver must still recover `A=int, B=string`). */
  private instanceInfo = new Map<string, { head: string; args: Type[] }>();
  /** Generic impls on user structs, kept by struct head name until the struct is
   *  instantiated (`impl<T> Show2 for Box<T>` → specialized per `Box<…>`). */
  private genericImpls = new Map<string, ast.ImplDecl[]>();
  /** Ids of already-emitted functions (see `pushFunc`). */
  private emitted = new Set<FuncId>();

  private funcs: CoreFunc[] = [];

  constructor(private program: ast.Program, private plan: LockPlan | undefined, private elab: Elaboration) {
    const builtinE = builtinEnums().filter((e) => !ast.shadowsPrelude(program, e.name));
    const builtinT = builtinTraits().filter((t) => !ast.shadowsPrelude(program, t.name));
    // Built-in structs (`Pair<A, B>`, what a Dict yields when iterated) are generic
    // templates too — instantiated on demand like any user generic struct. A
    // `builtin struct` (a container like `Dict`) is excluded: it's a checker/tooling
    // declaration only — the backend provides the collection natively (recognized by
    // `isGenericContainer` + runtime tags), so it must not be lowered as a Go struct.
    const builtinS = builtinStructs().filter((s) => !s.builtin && !ast.shadowsPrelude(program, s.name));
    // Prelude free functions written in STM (`min`/`max`, `awaitAll`) are real,
    // generic functions: registered here so a call monomorphizes their body per
    // concrete type (skipped when shadowed by a program's own declaration of the
    // name).
    const preludeF = preludeFns().filter((f) => !ast.shadowsPrelude(program, f.name));
    for (const t of builtinT) this.traits.set(t.name, t);
    for (const d of [...builtinE, ...builtinS, ...preludeF, ...program.decls]) {
      if (d.kind === "struct") {
        this.structs.set(d.name, d);
        if (ast.isValueType(d)) this.valueTypes.add(d.name);
      } else if (d.kind === "enum") {
        this.enums.set(d.name, d);
        for (const v of d.variants) this.variantOf.set(v.name, { enumName: d.name, variant: v });
      } else if (d.kind === "fn") {
        this.fns.set(d.name, d);
      } else if (d.kind === "trait") {
        this.traits.set(d.name, d);
      }
    }
    // Built-in impls (the iterator adapters behind `Iterable.map`, the `Range`
    // iterable) register like any other; user impls come last so a user impl on the
    // same type would win. Drop any built-in impl whose target type the program
    // shadows with its own declaration (mirrors the built-in struct/enum filter).
    const shadowedTypes = new Set(
      program.decls.filter((d) => d.kind === "struct" || d.kind === "enum").map((d) => d.name)
    );
    // The stdlib `Range` is in effect (not shadowed), so a `for` over a `Range` has
    // the known start..end-1/+1 semantics `lowerRangeFor` lowers to a counted loop.
    this.stdlibRangeActive = !shadowedTypes.has("Range") && !shadowedTypes.has("RangeIter");
    const builtinI = builtinImpls().filter((im) => !shadowedTypes.has(im.typeName));
    for (const d of [...builtinI, ...program.decls]) if (d.kind === "impl") this.registerImpl(d);
    // A container's *inherent* impl also carries STM-bodied derived methods
    // (`Set.union`/`intersection`/`difference`) — kept out of `builtinImpls` because
    // that list's other inherent methods (`add`/`get`/…) are native. Index only the
    // bodied ones, in their own map so they lower lazily at the call site (a program
    // that never calls them emits nothing) rather than eagerly like a struct method.
    for (const im of builtinContainerInherentImpls()) {
      if (shadowedTypes.has(im.typeName)) continue;
      for (const m of im.methods) {
        if (m.builtin) continue;
        let table = this.containerDerived.get(im.typeName);
        if (!table) this.containerDerived.set(im.typeName, (table = new Map()));
        table.set(m.name, this.asMethod(m));
      }
    }
  }

  /** Build a `Method` record from a trait/impl method declaration. `inheritedFrom`
   *  is set only for an unoverridden trait default (see the field's doc). */
  private asMethod(m: { name: string; params: ast.Param[]; returnType: ast.TypeExpr | null; body?: ast.Block | null; typeParams?: { name: string }[]; builtin?: boolean }, inheritedFrom?: Method["inheritedFrom"]): Method {
    return { name: m.name, params: m.params, returnType: m.returnType, body: m.body!, typeParams: (m.typeParams ?? []).map((tp) => tp.name), inheritedFrom, builtin: m.builtin };
  }

  private registerImpl(decl: ast.ImplDecl) {
    if (decl.typeParams.length > 0 && !ast.isGenericContainer(decl.typeName)) {
      // A generic impl on a user struct (`impl<T> Show2 for Box<T>`): keep it until
      // each `Box<…>` is instantiated, then specialize its methods for that instance.
      const list = this.genericImpls.get(decl.typeName) ?? [];
      list.push(decl);
      this.genericImpls.set(decl.typeName, list);
      return;
    }
    let table = this.methodImpls.get(decl.typeName);
    if (!table) this.methodImpls.set(decl.typeName, (table = new Map()));
    const inherited = { trait: decl.trait, traitArgs: decl.traitArgs };
    const overridden = new Set(decl.methods.map((m) => m.name));
    for (const m of this.traits.get(decl.trait)?.methods ?? []) {
      if (m.body && !overridden.has(m.name)) table.set(m.name, this.asMethod(m, inherited));
    }
    for (const m of decl.methods) table.set(m.name, this.asMethod(m));
    const impls = this.traitImpls.get(decl.trait) ?? [];
    if (!impls.includes(decl.typeName)) impls.push(decl.typeName);
    this.traitImpls.set(decl.trait, impls);
    // A user type's `Iterable<E>`/`Iterator<E>` element, for `for x in <instance>`.
    // Skipped for a generic container: it iterates via the native collection path
    // (see `lowerFor`), not this map — and resolving its element arg here with the
    // container's parameters unbound would instantiate a junk `Pair<unknown, …>`.
    if (
      (decl.trait === "Iterable" || decl.trait === "Iterator") &&
      decl.traitArgs[0] &&
      !ast.isGenericContainer(decl.typeName)
    ) {
      this.iterElem.set(decl.typeName, this.resolveType(decl.traitArgs[0]));
    }
  }

  run(): CoreProgram {
    // Plain functions. A generic function isn't lowered here — it's monomorphized
    // on demand at each call site (see `instantiateFn`), one CoreFunc per concrete
    // type-argument tuple.
    for (const fn of this.fns.values()) {
      if (fn.typeParams.length > 0) continue;
      this.pushFunc(this.lowerFunc(fn.name, undefined, fn.params, fn.returnType, fn.body));
    }
    // Every installed method (impl methods + materialized trait defaults),
    // specialized to its target type — this is the monomorphization. (Methods on a
    // generic struct's instantiations are lowered by `instantiateStruct` instead,
    // as the instances come into existence; `pushFunc` dedupes the two paths.)
    for (const [typeName, table] of [...this.methodImpls]) {
      if (this.structInstances.has(typeName)) continue; // instance methods: see instantiateImpl
      // Primitive impls (`impl str`/`impl char`, and the `impl Ord for int`/`float`/
      // `bool`/… in the prelude) are lowered *on demand* at each call site (see
      // `lowerMethodCall`), like generic methods — so a program that never calls a
      // scalar method (e.g. `x.cmp(y)`) pays nothing for one.
      if (PRIMITIVE_IMPL_KEYS.has(typeName)) continue;
      for (const m of table.values()) {
        // A `builtin` method has no body to lower — a call to it becomes a `builtin`
        // Core node, plugged in natively by the backend (see `lowerMethodCall`).
        if (m.builtin) continue;
        // Instantiated per call rather than lowered here: a generic method, and a
        // default inherited from a *generic* trait (its body is generic over the
        // trait's parameters, bound to the impl's trait arguments only at the call
        // site — see `isDeferredDefault`). `map`/`reduce`/`filter` take that path.
        if (m.typeParams.length > 0 || this.isDeferredDefault(m)) continue;
        this.pushFunc(this.lowerFunc(`${typeName}_${m.name}`, typeName, m.params, m.returnType, m.body));
      }
    }
    if (!this.fns.has("main")) throw new MonoError("no main function");
    // A collection reaches the program either by a literal construction (which
    // records its cursor — see `collectionNew`) or as a struct field left empty at
    // construction. Record the cursor for every container-typed field too, so such a
    // field is a genuine `Iterable` (its `collRec.iterFn` is installed) rather than
    // type-checking as one and panicking when iterated — the whole point of design C.
    this.recordContainerFieldAdapters();
    // Built after all bodies are lowered: a generic struct's declaration is abstract
    // (its fields mention type parameters), so only its concrete instantiations —
    // materialized on demand during lowering — are emitted, alongside the plain ones.
    const structs: CoreStruct[] = [
      ...[...this.structs.values()]
        .filter((s) => s.typeParams.length === 0)
        .map((s) => ({
          name: s.name,
          valueType: this.valueTypes.has(s.name),
          fields: s.fields.map((f) => ({ name: f.name, type: this.resolveType(f.type), key: f.key === true })),
        })),
      ...this.structInstances.values(),
    ];
    // Enums mirror structs: a generic enum's declaration is abstract, so only its
    // concrete instantiations are emitted (each with concrete payloads), alongside
    // the non-generic ones.
    const enums: CoreEnum[] = [
      ...[...this.enums.values()]
        .filter((e) => e.typeParams.length === 0)
        .map((e) => ({
          name: e.name,
          variants: e.variants.map((v) => ({
            name: v.name,
            fields: v.fields.map((f) => ({ name: f.name, type: this.resolveType(f.type), key: f.key === true })),
          })),
        })),
      ...this.enumInstances.values(),
    ];
    const memory: CoreMemoryMode = this.plan?.mode ?? "mvcc";
    // Built once all bodies are lowered, so `dynDispatched` holds every dynamically
    // dispatched (trait, method) — the resolved tables the backend prints.
    const dispatch = [...this.dynDispatched].map((key) => {
      const [trait, method] = key.split("#") as [string, string];
      return this.dispatchTable(trait, method);
    });
    return { structs, enums, funcs: this.funcs, mainId: "main", memory, dispatch, iterAdapters: this.iterAdapters };
  }

  /** Record the iterator cursor for every `Set`/`Dict`/`List`-typed struct field, so
   *  a container that only ever exists as an empty field is still iterable (its
   *  cursor, `Option`, and `Pair` instances are emitted, and its `iterFn` can be
   *  installed). A field's element types introduce no *new* containers — a cursor's
   *  own `coll` field is the same container — so a single pass over the current
   *  structs closes the set. Runs after all bodies are lowered, before the
   *  struct/enum lists build. */
  private recordContainerFieldAdapters() {
    const record = (t: Type) => {
      if (t.k === "generic" && (t.name === "Set" || t.name === "Dict" || t.name === "List")) {
        this.containerAdapter(t.name, t.args);
      }
    };
    for (const decl of this.structs.values()) {
      if (decl.typeParams.length > 0) continue; // generic decls are abstract; see instances
      for (const f of decl.fields) record(this.resolveType(f.type));
    }
    for (const inst of [...this.structInstances.values()]) {
      for (const f of inst.fields) record(f.type);
    }
  }

  /** Append a lowered function, deduping by id — an instantiated generic-struct
   *  method can be reached both on demand and by the run() sweep over methodImpls. */
  private pushFunc(fn: CoreFunc) {
    if (this.emitted.has(fn.id)) return;
    this.emitted.add(fn.id);
    this.funcs.push(fn);
  }

  /** Resolve one `(trait, method)` into a dispatch table: the abstract stub
   *  signature plus, per implementing type, the concrete `Type_method` to forward
   *  to (each already lowered as a CoreFunc). `Self` stays the trait here (the
   *  backend renders it as its top type; each case asserts to the impl's type). */
  private dispatchTable(trait: string, method: string): CoreDispatch {
    const sig = this.traits.get(trait)?.methods.find((m) => m.name === method);
    if (!sig) throw new MonoError(`trait \`${trait}\` has no method \`${method}\``);
    const cases = (this.traitImpls.get(trait) ?? []).map((typeName) => ({
      type: this.namedType(typeName),
      target: `${typeName}_${method}` as FuncId,
    }));
    // A return that mentions the trait's own type parameters (or `Self`) is
    // specialized differently by each impl — `Iterator<T>.next()` returns a
    // distinct `Option__T` per element type — so the single shared stub can't name
    // one; it returns `any` and the call site asserts (see the `dynCall` branch).
    const generic = new Set([...(this.traits.get(trait)?.typeParams.map((tp) => tp.name) ?? []), "Self"]);
    return {
      trait,
      method,
      params: sig.params.map((p) => this.traitMethodType(p.type, trait)),
      ret: !sig.returnType ? VOID : this.typeExprMentions(sig.returnType, generic) ? UNKNOWN : this.traitMethodType(sig.returnType, trait),
      cases,
    };
  }

  /** Resolve a trait method's parameter/return type for a *dynamic* context, where
   *  `Self` is the trait itself (no single concrete receiver). */
  private traitMethodType(te: ast.TypeExpr, trait: string): Type {
    return "name" in te && te.name === "Self" ? { k: "trait", name: trait, args: [] } : this.resolveType(te);
  }

  /** The concrete return type of a generic *builtin* call: infer its type parameters
   *  from the argument types (and any explicit type args), then resolve the declared
   *  return type under those bindings. Like `instantiateFn` but with no body to
   *  lower — a builtin is implemented natively, so only its type flows into Core. */
  private genericBuiltinReturn(fn: ast.FnDecl, expr: ast.CallExpr): Type {
    const tps = fn.typeParams.map((tp) => tp.name);
    const bindings = this.callTypeBindings(tps, expr);
    const prev = this.typeBindings;
    this.typeBindings = bindings;
    try {
      return fn.returnType ? this.resolveType(fn.returnType) : VOID;
    } finally {
      this.typeBindings = prev;
    }
  }

  /** The type-parameter bindings for a generic free-function / builtin call. The
   *  checker inferred these type arguments — including cross-branch/contextual
   *  refinement the backend can't reproduce — and recorded them in the elaboration,
   *  so the monomorphizer *consumes* them, lowered under the current instantiation's
   *  bindings (`lowerType`), rather than re-inferring. A generic free function is
   *  only ever called with inferred type arguments, so the checker always records an
   *  entry; a missing one is a compiler invariant violation. */
  private callTypeBindings(tps: string[], expr: ast.CallExpr): Map<string, Type> {
    const recorded = this.elab.typeArgs.get(expr);
    if (!recorded || recorded.length !== tps.length)
      throw new MonoError(`missing recorded type arguments for a generic call (${tps.length} expected)`);
    const bindings = new Map<string, Type>();
    tps.forEach((tp, i) => bindings.set(tp, this.lowerType(recorded[i]!)));
    return bindings;
  }

  /** Monomorphize a generic function for one call: read its inferred type arguments,
   *  then lower a specialized copy (once per concrete tuple) with the type parameters
   *  bound. Returns the instance's mangled id and concrete return type. */
  private instantiateFn(fn: ast.FnDecl, expr: ast.CallExpr): { id: FuncId; ret: Type } {
    const tps = fn.typeParams.map((tp) => tp.name);
    const bindings = this.callTypeBindings(tps, expr);
    const typeArgs = tps.map((tp) => bindings.get(tp) ?? UNKNOWN);
    const id: FuncId = `${fn.name}__${typeArgs.map(mangleType).join("_")}`;
    const memo = this.fnInstances.get(id);
    if (memo) return memo;

    const prev = this.typeBindings;
    this.typeBindings = bindings;
    try {
      const ret = fn.returnType ? this.resolveType(fn.returnType) : VOID;
      const result = { id, ret };
      this.fnInstances.set(id, result); // memoize before lowering (recursion-safe)
      this.pushFunc(this.lowerFunc(id, undefined, fn.params, fn.returnType, fn.body));
      return result;
    } finally {
      this.typeBindings = prev;
    }
  }

  /** Monomorphize a generic method (`fn map<U>(…)`) for one call: infer its own type
   *  arguments from the call, then lower a specialized copy bound to `self`'s type and
   *  those arguments. Mirrors `instantiateFn`, but the receiver is `args[0]`. */
  private instantiateMethod(selfTypeName: string, method: string, m: Method, expr: ast.CallExpr, recv: CoreExpr, env: Env): CoreExpr {
    // Base bindings — the type parameters in scope in the method body that are fixed
    // by the receiver (the method's own `<U>` is inferred from the arguments below):
    //   * an inherited trait default is generic over the *trait's* parameters, bound
    //     to the receiver's trait arguments (`Iterable<int>` on `Countdown` ⇒ `T=int`);
    //   * an explicit method sees the *struct's* parameters (`Box__int` ⇒ `T=int`).
    const base = this.methodBaseBindings(selfTypeName, m, recv.type);
    const bindings = new Map(base);
    // Bind the method's *own* type parameters (`map<U>`'s `U`) from the checker's
    // recorded type arguments. A method with none — a trait default like `filter`,
    // deferred only because its trait is generic — is bound entirely by the receiver
    // `base`, and the checker records nothing (there is nothing to record). With
    // every binding known up front, each argument (a lambda included) is lowered once
    // with its expected parameter type already concrete — no two-pass inference.
    if (m.typeParams.length > 0) {
      const recorded = this.elab.typeArgs.get(expr);
      if (!recorded || recorded.length !== m.typeParams.length)
        throw new MonoError(`missing recorded type arguments for \`${selfTypeName}.${method}\``);
      m.typeParams.forEach((tp, i) => bindings.set(tp, this.lowerType(recorded[i]!)));
    }
    const args: CoreExpr[] = expr.args.map((a, i) => {
      const expected = m.params[i] ? this.withBindings(bindings, () => this.resolveType(m.params[i]!.type)) : undefined;
      return this.lowerExpr(a.value, env, expected);
    });
    const typeArgs = m.typeParams.map((tp) => bindings.get(tp) ?? UNKNOWN);
    const id: FuncId = `${selfTypeName}_${method}__${typeArgs.map(mangleType).join("_")}`;
    let memo = this.fnInstances.get(id);
    if (!memo) {
      const prev = this.typeBindings;
      this.typeBindings = bindings;
      try {
        memo = { id, ret: m.returnType ? this.resolveType(m.returnType) : VOID };
        this.fnInstances.set(id, memo); // memoize before lowering (recursion-safe)
        this.pushFunc(this.lowerFunc(id, selfTypeName, m.params, m.returnType, m.body, recv.type));
      } finally {
        this.typeBindings = prev;
      }
    }
    return { e: "staticCall", target: id, args: [recv, ...(args as CoreExpr[])], type: memo.ret };
  }

  /** Whether a method must be lowered per call rather than eagerly: a default
   *  inherited from a *generic* trait. Its body is generic over the trait's type
   *  parameters (`Iterable<T>`'s `filter` mentions `T`), which are known only from
   *  the impl's trait arguments at a call site — so `instantiateMethod` handles it,
   *  binding them via `methodBaseBindings` (like a generic method). A default from a
   *  non-generic trait (`Shape`'s `describe`) has nothing to bind and is lowered
   *  eagerly, so it can also back a dynamic-dispatch table. */
  private isDeferredDefault(m: Method): boolean {
    return m.inheritedFrom != null && (this.traits.get(m.inheritedFrom.trait)?.typeParams.length ?? 0) > 0;
  }

  /** The receiver-fixed type bindings for a generic method call (see `instantiateMethod`):
   *  a trait default's binds the trait's parameters (resolved via the receiver's own
   *  bindings); an explicit method's are the receiver struct's bindings. */
  private methodBaseBindings(selfTypeName: string, m: Method, recvType?: Type): Map<string, Type> {
    const structBindings = this.containerReceiverBindings(recvType) ?? this.structTypeBindings(selfTypeName);
    if (!m.inheritedFrom) return structBindings;
    const base = new Map<string, Type>();
    const trait = this.traits.get(m.inheritedFrom.trait);
    trait?.typeParams.forEach((tp, i) => {
      const arg = m.inheritedFrom!.traitArgs[i];
      if (arg) base.set(tp.name, this.withBindings(structBindings, () => this.resolveType(arg)));
    });
    return base;
  }

  /** A generic container receiver's type parameters bound to its arguments — the
   *  container analogue of `structTypeBindings`, read off the receiver's type
   *  (`Set<int>` ⇒ `{T: int}`, `Dict<str, int>` ⇒ `{K: str, V: int}`). This is what
   *  lets a container's `Iterable` defaults (`map`/`filter`/`reduce`) resolve their
   *  element type when instantiated. Null for a non-container receiver. */
  private containerReceiverBindings(recvType?: Type): Map<string, Type> | null {
    if (recvType?.k !== "generic") return null;
    const params = containerTypeParams(recvType.name);
    if (!params.length) return null;
    return new Map(params.map((name, i) => [name, recvType.args[i] ?? UNKNOWN] as const));
  }

  /** Record (once) the native cursor a `Set<E>` / `Dict<K, V>` needs, and return its
   *  instance names. `iterFn` (`Set__int_iter`) builds a fresh cursor over the
   *  collection; the backend installs it on the collection at construction
   *  (`collRec.iterFn`) and calls it for a direct `container.iter()` and from the
   *  `Iterable.iter` dispatch — so a container is iterated *as itself*, no wrapping
   *  at the use site. The cursor's `next` yields an `Option<E>` (an `Option<Pair>`
   *  for a Dict), so those instances are materialized here too — the backend emits
   *  `iter`/`next` natively and can't re-mangle the instance names itself. */
  private containerAdapter(container: "Set" | "Dict" | "List", args: Type[]): import("./core.js").IterAdapter {
    const cursorStruct = container === "Set" ? "SetIter" : container === "List" ? "ListIter" : "DictIter";
    const cursor = this.instantiateStruct(cursorStruct, args);
    const existing = this.iterAdapters.find((a) => a.struct === cursor);
    if (existing) return existing;
    const iterFn = `${container}__${args.map(mangleType).join("_")}_iter`;
    let adapter: import("./core.js").IterAdapter;
    if (container === "Set" || container === "List") {
      // A Set yields its element (a membership key); a List yields its element (the
      // stored value). Both narrow a single snapshot slot to the element type.
      const elem = args[0] ?? UNKNOWN;
      adapter = { struct: cursor, container, iterFn, option: this.instantiateEnum("Option", [elem]), elem };
    } else {
      const [k, v] = [args[0] ?? UNKNOWN, args[1] ?? UNKNOWN];
      const pair = this.instantiateStruct("Pair", [k, v]);
      const option = this.instantiateEnum("Option", [{ k: "struct", name: pair }]);
      adapter = { struct: cursor, container, iterFn, option, elem: k, value: v, pair };
    }
    this.iterAdapters.push(adapter);
    return adapter;
  }

  /** The type parameters of a generic-struct instance bound to its type arguments
   *  (`Box__int` ⇒ `{T: int}`); empty for a non-generic struct. */
  private structTypeBindings(structName: string): Map<string, Type> {
    const info = this.instanceInfo.get(structName);
    if (!info) return new Map();
    const decl = this.structs.get(info.head)!;
    return new Map(decl.typeParams.map((tp, i) => [tp.name, info.args[i] ?? UNKNOWN] as const));
  }

  /** Run `fn` with `bindings` as the active type-variable substitution, restoring it
   *  after (so resolving a type under a receiver's bindings doesn't leak out). */
  private withBindings<T>(bindings: Map<string, Type>, fn: () => T): T {
    const prev = this.typeBindings;
    this.typeBindings = bindings;
    try {
      return fn();
    } finally {
      this.typeBindings = prev;
    }
  }

  /** A struct field's concrete type, or null if the struct has no such field —
   *  distinguishes a data field (e.g. a function-typed `step`) from a method call. */
  private fieldTypeOrNull(structName: string, field: string): Type | null {
    const instance = this.structInstances.get(structName);
    if (instance) return instance.fields.find((f) => f.name === field)?.type ?? null;
    const fd = this.structs.get(structName)?.fields.find((f) => f.name === field);
    return fd ? this.resolveType(fd.type) : null;
  }

  /** Bind type parameters by matching a declared type expression against a concrete
   *  argument type (`T` ↦ `Set<int>`; `Pair<A, B>` ↦ `A`, `B` structurally). */
  /** Lower one function/method body in a concrete context. `selfType`, when given,
   *  names the target type — `self` is bound to it and `Self` resolves to it. */
  private lowerFunc(
    id: FuncId,
    selfType: string | undefined,
    params: ast.Param[],
    returnType: ast.TypeExpr | null,
    body: ast.Block,
    selfConcrete?: Type
  ): CoreFunc {
    const prevSelf = this.self;
    const prevRet = this.currentRet;
    // A specialized container method knows its concrete `self` type (`Set<int>`),
    // whose element args `namedType` can't recover from the bindings (a `Dict`'s
    // `K`/`V` aren't in scope in an inherited `Iterable` default). Use it directly so
    // `for x in self` inside the body binds the real element type.
    this.self =
      selfConcrete?.k === "generic" ? selfConcrete : selfType ? this.namedType(selfType) : null;
    try {
      const env = new Env();
      let self: CoreFunc["self"];
      if (selfType) {
        self = { name: "self", type: this.self! };
        env.declare("self", this.self!);
      }
      const coreParams = params.map((p) => {
        const t = this.resolveType(p.type);
        env.declare(p.name, t);
        return { name: p.name, type: t };
      });
      const ret = returnType ? this.resolveType(returnType) : VOID;
      this.currentRet = ret; // so a `return <lambda>` knows the expected function type
      return { id, self, params: coreParams, ret, body: this.lowerBlock(body, env) };
    } finally {
      this.self = prevSelf;
      this.currentRet = prevRet;
    }
  }

  // -- statements ---------------------------------------------------------

  private lowerBlock(block: ast.Block, env: Env): CoreStmt[] {
    const inner = env.child();
    return block.stmts.map((s) => this.lowerStmt(s, inner));
  }

  private lowerStmt(stmt: ast.Stmt, env: Env): CoreStmt {
    switch (stmt.kind) {
      case "let": {
        const value = this.lowerExpr(stmt.value, env);
        const declared = stmt.type ? this.resolveType(stmt.type) : value.type;
        env.declare(stmt.name, declared);
        return { s: "let", name: stmt.name, type: declared, value };
      }
      case "assign": {
        const rhs = this.lowerExpr(stmt.value, env);
        if (stmt.target.kind === "ident") {
          const name = stmt.target.name;
          // A first `x = e` binding is a declaration (`let`); only a re-assignment
          // (or a compound op) is an `assign`. Core keeps the two distinct so a
          // backend needn't re-derive which one it is.
          if (stmt.op === "=" && !env.lookup(name)) {
            env.declare(name, rhs.type);
            return { s: "let", name, type: rhs.type, value: rhs };
          }
          const type = env.lookup(name) ?? rhs.type;
          // A compound op is lowered away like any binary: `x += e` becomes a plain
          // `x = binaryOp("+", x, e)`, so operator overloading resolves here too
          // (`acc += v` on a Vec2 → `acc = Vec2_add(acc, v)`) and Core stays op-free.
          const value = stmt.op === "=" ? rhs : this.binaryOp(stmt.op.slice(0, -1), { e: "local", name, type }, rhs, type);
          return { s: "assign", target: { lv: "local", name, type }, value };
        }
        const fa = stmt.target as ast.FieldAccess;
        const object = this.lowerExpr(fa.object, env);
        const type = this.fieldType(object.type, fa.field, fa.span);
        const value =
          stmt.op === "=" ? rhs : this.binaryOp(stmt.op.slice(0, -1), { e: "field", object, field: fa.field, type }, rhs, type);
        return { s: "assign", target: { lv: "field", object, field: fa.field, type }, value };
      }
      case "exprStmt":
        return { s: "expr", expr: this.lowerExpr(stmt.expr, env) };
      case "return":
        return { s: "return", value: stmt.value ? this.lowerExpr(stmt.value, env, this.currentRet ?? undefined) : null };
      case "if":
        return {
          s: "if",
          cond: this.lowerExpr(stmt.cond, env),
          then: this.lowerBlock(stmt.then, env),
          else: stmt.else
            ? stmt.else.kind === "if"
              ? [this.lowerStmt(stmt.else, env)]
              : this.lowerBlock(stmt.else, env)
            : null,
        };
      case "while":
        return { s: "while", cond: this.lowerExpr(stmt.cond, env), body: this.lowerBlock(stmt.body, env) };
      case "for":
        return this.lowerFor(stmt, env);
      case "match":
        return this.lowerMatch(stmt, env);
      case "go": {
        if (stmt.call.callee.kind !== "ident") throw new MonoError("go requires a direct call");
        const name = stmt.call.callee.name;
        const fn = this.fns.get(name);
        if (!fn) throw new MonoError(`go: unknown function \`${name}\``);
        // Lower args (pinning a lambda arg by its parameter type), then resolve the
        // target: a generic thread body (`go completeWith(f, work)` where
        // `completeWith<T>`) must be monomorphized to its concrete instantiation and
        // emitted, exactly as a `staticCall` would — otherwise the backend emits a
        // call to a function that was never generated.
        const args = stmt.call.args.map((a, i) =>
          this.lowerExpr(a.value, env, a.value.kind === "lambda" && fn.params[i] ? this.resolveType(fn.params[i]!.type) : undefined)
        );
        const target =
          fn.typeParams.length > 0 ? this.instantiateFn(fn, stmt.call).id : name;
        return { s: "go", target, args };
      }
      case "txn": {
        // Bake in the LockPlan's decision for this txn (keyed by AST node); the
        // shapes match, so the strategy passes straight through to Core. Without a
        // plan, every txn is optimistic — always correct, just never 2PL.
        const strategy: CoreTxnStrategy = this.plan?.strategies.get(stmt) ?? { kind: "occ" };
        return { s: "txn", body: this.lowerBlock(stmt.body, env), strategy };
      }
      case "block":
        return { s: "block", body: this.lowerBlock(stmt, env) };
      case "retry":
        // A bare STM blocking primitive: unwind to the enclosing OCC `txn`, which
        // parks on its read set (see the Go runtime's `Run`/`retryPark` and the
        // interpreter's `execOccTxn`). It carries no operands.
        return { s: "retry" };
      case "raise":
      case "try":
        // Exceptions run on the interpreter today. Lowering them adds two Core
        // statement nodes and a `panic`/`recover` seam in the Go runtime — staged as
        // its own step (see docs/design/exceptions.md#staging), so until then a
        // program using them is rejected here rather than silently mis-compiled.
        throw new MonoError(
          `\`${stmt.kind}\` is not lowered to Core yet: exceptions run on the interpreter (\`stm run\`), not the Go backend`
        );
      case "assert":
        // `assert cond, "msg"` lowers to the `assert` builtin — the same shape as
        // `print`, and soft in the same way the interpreter's is: it records the
        // verdict and the program carries on. The backends differ only in where the
        // verdict goes: a trace event the timeline search reads, versus stderr and a
        // non-zero exit — because only one of the two has a timeline search.
        return {
          s: "expr",
          expr: {
            e: "builtin",
            name: "assert",
            args: [
              this.lowerExpr(stmt.cond, env),
              stmt.message
                ? this.lowerExpr(stmt.message, env)
                : { e: "string", value: "", type: STRING },
            ],
            type: VOID,
          },
        };
    }
  }

  private lowerFor(stmt: ast.ForStmt, env: Env): CoreStmt {
    const loopEnv = env.child();
    const value = this.lowerExpr(stmt.iterable, env);
    const t = value.type;
    if (t.k === "generic" && (t.name === "Set" || t.name === "Dict" || t.name === "List")) {
      // A Set/List yields its elements; a Dict yields a `Pair<K, V>` per entry, which
      // instantiates to a concrete struct now that generic structs are monomorphized.
      const elem: Type =
        t.name === "Dict"
          ? { k: "struct", name: this.instantiateStruct("Pair", [t.args[0] ?? UNKNOWN, t.args[1] ?? UNKNOWN]) }
          : t.args[0] ?? UNKNOWN;
      loopEnv.declare(stmt.varName, elem);
      return {
        s: "for",
        varName: stmt.varName,
        varType: elem,
        iter: { it: "collection", container: t.name, value, elem },
        body: this.lowerBlock(stmt.body, loopEnv),
      };
    }
    // Peephole (Core-level, so every backend prints it): a `for` over the stdlib
    // `Range` — which yields `start, start+1, …, end-1` — lowers to a plain counted
    // loop built from ordinary Core (`block`/`let`/`while`/`assign`). No range concept
    // enters the IR, and the `RangeIter` cursor (and, inside a `txn`, its per-step
    // versioned read/write) is never allocated. A shadowed `Range` falls through to
    // the normal protocol below.
    const typeName = t.k === "struct" || t.k === "enum" ? t.name : null;
    if (typeName === "Range" && this.stdlibRangeActive) {
      return this.lowerRangeFor(stmt, value, loopEnv);
    }
    // A user Iterable/Iterator: decide the protocol by trait implementation.
    if (typeName && (this.implementsTrait(typeName, "Iterable") || this.implementsTrait(typeName, "Iterator"))) {
      const elem = this.iterElem.get(typeName) ?? UNKNOWN;
      loopEnv.declare(stmt.varName, elem);
      const body = this.lowerBlock(stmt.body, loopEnv);
      if (this.implementsTrait(typeName, "Iterable")) {
        const iterFn = this.methodImpls.get(typeName)!.get("iter")!;
        // Resolve the iterator type under the receiver's own type bindings: for a
        // generic iterable (`MapIterable__int_int`), `iter()` returns `MapIterator<T, U>`,
        // whose `T`/`U` are the receiver's type arguments — without them the iterator
        // would resolve to an `unknown` instantiation and miss the real `_next`.
        const iterType = this.withBindings(this.structTypeBindings(typeName), () =>
          this.resolveType(iterFn.returnType!, typeName),
        );
        const iterName = iterType.k === "struct" || iterType.k === "enum" ? iterType.name : null;
        if (!iterName) throw new MonoError(`iter() on \`${typeName}\` must return a concrete iterator`);
        return {
          s: "for",
          varName: stmt.varName,
          varType: elem,
          iter: {
            it: "iterable",
            value,
            iter: { via: "static", target: `${typeName}_iter` },
            next: { via: "static", target: `${iterName}_next` },
            elem,
            option: this.nextOptionType(iterName),
          },
          body,
        };
      }
      return {
        s: "for",
        varName: stmt.varName,
        varType: elem,
        iter: { it: "iterator", value, next: { via: "static", target: `${typeName}_next` }, elem, option: this.nextOptionType(typeName) },
        body,
      };
    }
    // A trait-typed iterable/iterator (`for x in it` where `it: Iterable<int>`):
    // drive it through *dynamic* dispatch — an Iterable yields its iterator via
    // `iter()`, then `next()` until `None`. The concrete impl is chosen at runtime
    // from the value's type (the same tables a method call on the trait uses), so
    // this iterates any Iterable — including a container, which dispatches straight
    // to its installed cursor-maker (`collRec.iterFn`), no wrapping at the slot.
    if (t.k === "trait" && (t.name === "Iterable" || t.name === "Iterator")) {
      const elem = t.args[0] ?? UNKNOWN;
      loopEnv.declare(stmt.varName, elem);
      const body = this.lowerBlock(stmt.body, loopEnv);
      const option = this.instantiateEnum("Option", [elem]);
      this.dynDispatched.add("Iterator#next");
      if (t.name === "Iterator") {
        return { s: "for", varName: stmt.varName, varType: elem, iter: { it: "iterator", value, next: { via: "dynamic", trait: "Iterator", method: "next" }, elem, option }, body };
      }
      this.dynDispatched.add("Iterable#iter");
      return {
        s: "for",
        varName: stmt.varName,
        varType: elem,
        iter: {
          it: "iterable",
          value,
          iter: { via: "dynamic", trait: "Iterable", method: "iter" },
          next: { via: "dynamic", trait: "Iterator", method: "next" },
          elem,
          option,
        },
        body,
      };
    }
    throw new MonoError(`value is not iterable (${t.k})`);
  }

  /** Lower `for x in <Range>` to a counted loop over ordinary Core:
   *
   *    { let __rng_i = <start>; let __rng_end = <end>
   *      while __rng_i < __rng_end { let x = __rng_i; <body>; __rng_i = __rng_i + 1 } }
   *
   * `<start>`/`<end>` come straight from an inline `Range(start, end)` construction (so
   * no `Range` object is even built); otherwise the range value is bound once and its
   * fields read. Temp names are prefixed and each loop sits in its own `block`, so
   * nested range loops scope cleanly. This keeps a backend a plain printer — the
   * counted loop is expressed entirely in existing Core nodes. */
  private lowerRangeFor(stmt: ast.ForStmt, value: CoreExpr, loopEnv: Env): CoreStmt {
    const iLocal: CoreExpr = { e: "local", name: "__rng_i", type: INT };
    const prelude: CoreStmt[] = [];
    let startExpr: CoreExpr;
    let endExpr: CoreExpr;
    if (value.e === "construct" && value.typeName === "Range") {
      // Inline `a..b`: take the bounds directly, skipping the throwaway `Range`.
      startExpr = value.fields.find((f) => f.name === "start")!.value;
      endExpr = value.fields.find((f) => f.name === "end")!.value;
    } else {
      // A range held in a variable/expression: evaluate it once, then read its bounds.
      const rng: CoreExpr = { e: "local", name: "__rng_r", type: value.type };
      prelude.push({ s: "let", name: "__rng_r", type: value.type, value });
      startExpr = { e: "field", object: rng, field: "start", type: INT };
      endExpr = { e: "field", object: rng, field: "end", type: INT };
    }
    loopEnv.declare(stmt.varName, INT);
    const body: CoreStmt[] = [
      { s: "let", name: stmt.varName, type: INT, value: iLocal },
      ...this.lowerBlock(stmt.body, loopEnv),
      {
        s: "assign",
        target: { lv: "local", name: "__rng_i", type: INT },
        value: { e: "binop", op: "+", left: iLocal, right: { e: "int", value: 1, type: INT }, type: INT },
      },
    ];
    return {
      s: "block",
      body: [
        ...prelude,
        { s: "let", name: "__rng_i", type: INT, value: startExpr },
        { s: "let", name: "__rng_end", type: INT, value: endExpr },
        {
          s: "while",
          cond: { e: "binop", op: "<", left: iLocal, right: { e: "local", name: "__rng_end", type: INT }, type: BOOL },
          body,
        },
      ],
    };
  }

  /** The monomorphized `Option<E>` instance that `<iterator>.next()` returns, read
   *  off that method's own resolved return type — so a `for` driver asserts the
   *  exact `_Some` variant the iterator yields (its element type may be `unknown`
   *  in `iterElem` yet concrete here, e.g. a lazy `MapIterator`). */
  private nextOptionType(iteratorTypeName: string): string {
    const nextM = this.methodImpls.get(iteratorTypeName)?.get("next");
    const ret = nextM?.returnType
      ? this.withBindings(this.structTypeBindings(iteratorTypeName), () =>
          this.resolveType(nextM.returnType!, iteratorTypeName)
        )
      : UNKNOWN;
    return ret.k === "enum" ? ret.name : this.instantiateEnum("Option", [UNKNOWN]);
  }

  private lowerMatch(stmt: ast.MatchStmt, env: Env): CoreStmt {
    const subject = this.lowerExpr(stmt.subject, env);
    if (subject.type.k !== "enum") throw new MonoError("match subject is not an enum");
    const enumName = subject.type.name;
    // A generic enum has been flattened to a concrete instance whose variant
    // payloads are already resolved; a non-generic enum resolves its (parameter-
    // free) fields directly. Either way a binding gets a real payload type.
    const inst = this.enumInstances.get(enumName);
    const variantFieldType = (variant: string, field: string): Type => {
      if (inst) return inst.variants.find((v) => v.name === variant)?.fields.find((f) => f.name === field)?.type ?? UNKNOWN;
      const fd = this.enums.get(enumName)?.variants.find((v) => v.name === variant)?.fields.find((f) => f.name === field);
      return fd ? this.resolveType(fd.type) : UNKNOWN;
    };
    const arms: CoreArm[] = stmt.arms.map((arm) => {
      const armEnv = env.child();
      const bindings = arm.bindings.map((b) => {
        const type = variantFieldType(arm.variant, b.field);
        armEnv.declare(b.name, type);
        return { name: b.name, field: b.field, type };
      });
      return { variant: arm.variant, bindings, body: this.lowerBlock(arm.body, armEnv), wildcard: arm.wildcard };
    });
    return { s: "match", subject, enumName, arms };
  }

  // -- expressions --------------------------------------------------------

  /** `expected` is the type this expression is used at (a callee's parameter, a
   *  function's return, …). It only matters for a lambda, whose parameter types the
   *  checker leaves abstract — they come from the context (see `lowerLambda`). */
  private lowerExpr(expr: ast.Expr, env: Env, expected?: Type): CoreExpr {
    switch (expr.kind) {
      case "int": return { e: "int", value: expr.value, type: INT };
      case "float": return { e: "float", value: expr.value, type: FLOAT };
      case "string": return { e: "string", value: expr.value, type: STRING };
      case "char": return { e: "char", value: expr.value, type: CHAR };
      case "bool": return { e: "bool", value: expr.value, type: BOOL };
      case "ident": {
        const type = env.lookup(expr.name);
        if (type) return { e: "local", name: expr.name, type };
        // A top-level function used as a value (`g = double`, `apply(double, …)`).
        const fn = this.fns.get(expr.name);
        if (fn && fn.typeParams.length === 0) {
          return { e: "funcRef", target: expr.name, type: this.fnType(fn) };
        }
        throw new MonoError(`undefined variable \`${expr.name}\``);
      }
      case "field": {
        const object = this.lowerExpr(expr.object, env);
        return { e: "field", object, field: expr.field, type: this.fieldType(object.type, expr.field, expr.span) };
      }
      case "unary": {
        const operand = this.lowerExpr(expr.operand, env);
        return { e: "unary", op: expr.op, operand, type: expr.op === "!" ? BOOL : operand.type };
      }
      case "list":
        return this.lowerListLit(expr, env, expected);
      case "brace":
        return this.lowerBraceLit(expr, env, expected);
      case "binary":
        return this.lowerBinary(expr, env);
      case "call":
        return this.lowerCall(expr, env, expected);
      case "lambda":
        return this.lowerLambda(expr, env, expected);
      case "ifExpr": {
        // The result type is what the checker typed the whole if-expression — read
        // it from the elaboration (lowered through this instantiation's bindings),
        // and lower each branch against it so an under-determined branch (`None()`)
        // and any int->float widening resolve to it.
        const type = this.lowerType(this.elab.types.get(expr) ?? UNKNOWN);
        return {
          e: "if",
          cond: this.lowerExpr(expr.cond, env),
          then: this.lowerExpr(expr.then, env, type),
          else: this.lowerExpr(expr.else, env, type),
          type,
        };
      }
    }
  }

  /** A list literal `[a, b, c]` -> `collectionLit`. The element type is the checker's
   *  (nothing is written at the literal), run through `lowerType` so a literal inside
   *  a generic body picks up *this* instantiation's bindings — `[x]` in
   *  `fn wrap<T>(x: T)` is a `List<int>` when called with an int, not a `List<T>`.
   *  The expected type is the fallback for an empty literal, which has no elements
   *  to have been inferred from. Like `collectionNew`, it installs the container's
   *  cursor-maker so the list is iterable as itself. */
  private lowerListLit(expr: ast.ListLit, env: Env, expected?: Type): CoreExpr {
    const elem = this.literalTypeArgs(expr, expected, "List", 1)[0] ?? UNKNOWN;
    const type: Type = { k: "generic", name: "List", args: [elem] };
    const iterFn = this.containerAdapter("List", [elem]).iterFn;
    const entries = expr.elements.map((e) => ({ value: this.lowerExpr(e, env, elem) }));
    return { e: "collectionLit", container: "List", entries, elem, type, iterFn };
  }

  /** A brace literal -> `collectionLit`, the `{ … }` counterpart of `lowerListLit`:
   *  same type sourcing, with a dict's entries carrying keys and a set's not. Which
   *  container it is comes from the checker's recorded type — the parser leaves an
   *  empty `{}` undecided, and the checker is where the context resolved it. */
  private lowerBraceLit(expr: ast.BraceLit, env: Env, expected?: Type): CoreExpr {
    const recorded = this.elab.types.get(expr);
    const container =
      expr.container ?? (recorded?.k === "generic" && recorded.name === "Set" ? "Set" : "Dict");
    if (container === "Set") {
      const elem = this.literalTypeArgs(expr, expected, "Set", 1)[0] ?? UNKNOWN;
      const type: Type = { k: "generic", name: "Set", args: [elem] };
      const iterFn = this.containerAdapter("Set", [elem]).iterFn;
      // A set's element IS its store key, so — like a list's — it rides in `value`
      // and the backend keys on it (see `collectionLit`).
      const entries = expr.entries.map((en) => ({ value: this.lowerExpr(en.value, env, elem) }));
      return { e: "collectionLit", container: "Set", entries, elem, type, iterFn };
    }
    const args = this.literalTypeArgs(expr, expected, "Dict", 2);
    const key = args[0] ?? UNKNOWN;
    const value = args[1] ?? UNKNOWN;
    const type: Type = { k: "generic", name: "Dict", args: [key, value] };
    const iterFn = this.containerAdapter("Dict", [key, value]).iterFn;
    const entries = expr.entries.map((en) => ({
      key: this.lowerExpr(en.key!, env, key),
      value: this.lowerExpr(en.value, env, value),
    }));
    return { e: "collectionLit", container: "Dict", entries, elem: key, value, type, iterFn };
  }

  /** A collection literal's concrete type arguments: the checker's recorded ones,
   *  lowered under this instantiation's bindings, falling back to the expected type
   *  (which is all an *empty* literal has) and then to `unknown`. */
  private literalTypeArgs(expr: ast.Expr, expected: Type | undefined, container: string, arity: number): Type[] {
    const recorded = this.elab.types.get(expr);
    const from = (t: Type | undefined) => (t?.k === "generic" && t.name === container ? t.args : []);
    const node = from(recorded);
    const ctx = from(expected);
    return Array.from({ length: arity }, (_, i) => this.lowerType(node[i] ?? ctx[i] ?? UNKNOWN));
  }

  /** A function's type as a Core `fn` type (its parameters and return). */
  private fnType(fn: ast.FnDecl): Type {
    return { k: "fn", params: fn.params.map((p) => this.resolveType(p.type)), ret: fn.returnType ? this.resolveType(fn.returnType) : VOID };
  }

  /** Lower an anonymous function. Parameter types come from an explicit annotation,
   *  else the expected function type (the context), else the checker's node type —
   *  the checker leaves lambda parameters abstract, so the context is what pins them.
   *  The body captures enclosing locals; Go closes over them lexically, but Core
   *  lists them so a backend that needs an explicit environment has it. */
  private lowerLambda(expr: ast.LambdaExpr, env: Env, expected?: Type): CoreExpr {
    const want = expected?.k === "fn" ? expected : undefined;
    const node = this.elab.types.get(expr);
    const nodeFn = node?.k === "fn" ? node : undefined;
    // A lambda parameter's type comes from its annotation, else the checker's recorded
    // node type, lowered through the current bindings (`(T) -> T` filling a field
    // records `(int) -> int`, which lowers to itself) — the same substitute-and-mangle
    // the return type uses below. `want` (an expected function type pushed by the
    // caller) still wins when present, but the node type now carries the answer, so no
    // field-type push is needed to pin it.
    const paramTypes = expr.params.map((p, i) =>
      p.type
        ? this.resolveType(p.type)
        : concrete(want?.params[i], nodeFn ? this.lowerType(nodeFn.params[i] ?? UNKNOWN) : undefined) ?? UNKNOWN
    );
    const child = env.child();
    const params = expr.params.map((p, i) => {
      child.declare(p.name, paramTypes[i]!);
      return { name: p.name, type: paramTypes[i]! };
    });
    // The lambda's result type is the checker's recorded return type, lowered through
    // the current bindings (`Option<int>` → `Option__int`) — the checker already
    // resolved it, including contextually (a bare `None()` in an `orElse` branch), so
    // the monomorphizer substitutes and mangles rather than re-inferring from the
    // body. The expected `want?.ret` wins when it is concrete (it may pin a return the
    // node type left as a method type variable); the lowered body type is only a
    // last-resort fallback when nothing was recorded.
    const nodeRet = nodeFn ? this.lowerType(nodeFn.ret) : undefined;
    let body: CoreStmt[];
    let ret: Type;
    if ("stmts" in expr.body) {
      ret = concrete(want?.ret, nodeRet) ?? VOID;
      body = this.lowerBlock(expr.body, child);
    } else {
      const value = this.lowerExpr(expr.body, child, want?.ret);
      ret = concrete(want?.ret, nodeRet, value.type) ?? value.type;
      body = [{ s: "return", value }];
    }
    const captures = this.freeVars(expr, env).map((name) => ({ name, type: env.lookup(name)! }));
    return { e: "closure", params, ret, captures, body, type: { k: "fn", params: paramTypes, ret } };
  }

  /** The enclosing-scope locals a lambda references (its captures): identifiers used
   *  in the body that resolve in `env` and aren't bound by the lambda itself. */
  private freeVars(expr: ast.LambdaExpr, env: Env): string[] {
    const bound = new Set(expr.params.map((p) => p.name));
    const used = new Set<string>();
    const visit = (n: unknown) => {
      if (!n || typeof n !== "object") return;
      const node = n as Record<string, unknown> & { kind?: string };
      if (node.kind === "ident" && typeof node.name === "string") used.add(node.name);
      if (node.kind === "let" && typeof node.name === "string") bound.add(node.name);
      if (node.kind === "lambda" && Array.isArray(node.params)) for (const p of node.params as { name: string }[]) bound.add(p.name);
      for (const v of Object.values(node)) {
        if (Array.isArray(v)) v.forEach(visit);
        else if (v && typeof v === "object") visit(v);
      }
    };
    visit(expr.body);
    return [...used].filter((name) => !bound.has(name) && env.lookup(name) !== undefined);
  }

  private lowerBinary(expr: ast.BinaryExpr, env: Env): CoreExpr {
    // The result type of an overloaded `a OP b` is what the checker typed the whole
    // expression — read it from the elaboration (lowered through the current bindings)
    // rather than re-resolving the operator method's declared return.
    const resultType = this.lowerType(this.elab.types.get(expr) ?? UNKNOWN);
    return this.binaryOp(expr.op, this.lowerExpr(expr.left, env), this.lowerExpr(expr.right, env), resultType);
  }

  /** Resolve `left OP right` on already-lowered operands. Operator overloading is
   *  lowered away here — on a user type, `a OP b` becomes a call — so both
   *  `lowerBinary` and compound assignment (`a += b`) share one code path.
   *  `resultType` is the type of the overall expression: the checker's recorded type
   *  for a plain `a OP b`, or the assignment target's type for a compound `a OP= b`
   *  (whose result is stored back into it). It types an overloaded arithmetic call;
   *  every other operator's result is fixed by the operator itself. */
  private binaryOp(op: string, left: CoreExpr, right: CoreExpr, resultType: Type): CoreExpr {
    if (op === "&&" || op === "||") {
      return { e: "binop", op, left, right, type: BOOL };
    }
    const recv = left.type;
    const userType = recv.k === "struct" || recv.k === "enum" ? recv.name : null;
    if (userType) {
      const table = this.methodImpls.get(userType);
      if (op in ARITH_METHOD && table?.has(ARITH_METHOD[op]!)) {
        const m = ARITH_METHOD[op]!;
        return { e: "staticCall", target: `${userType}_${m}`, args: [left, right], type: resultType };
      }
      if ((op === "==" || op === "!=") && table?.has("eq")) {
        const call: CoreExpr = { e: "staticCall", target: `${userType}_eq`, args: [left, right], type: BOOL };
        return op === "==" ? call : { e: "unary", op: "!", operand: call, type: BOOL };
      }
      if ((op === "<" || op === ">" || op === "<=" || op === ">=") && table?.has("cmp")) {
        const cmp: CoreExpr = { e: "staticCall", target: `${userType}_cmp`, args: [left, right], type: INT };
        return { e: "binop", op, left: cmp, right: { e: "int", value: 0, type: INT }, type: BOOL };
      }
    }
    if (op === "+" && recv.k === "string") return { e: "binop", op, left, right, type: STRING };
    if (op === "==" || op === "!=" || op === "<" || op === ">" || op === "<=" || op === ">=") {
      return { e: "binop", op, left, right, type: BOOL };
    }
    // arithmetic on scalars: float if either side is float
    const type = left.type.k === "float" || right.type.k === "float" ? FLOAT : INT;
    return { e: "binop", op, left, right, type };
  }

  private lowerCall(expr: ast.CallExpr, env: Env, expected?: Type): CoreExpr {
    if (expr.callee.kind === "field") return this.lowerMethodCall(expr, expr.callee, env);
    if (expr.callee.kind !== "ident") throw new MonoError("only direct or method calls are lowered");
    const name = expr.callee.name;
    const args = () => expr.args.map((a) => this.lowerExpr(a.value, env));

    if (this.structs.has(name)) {
      const decl = this.structs.get(name)!;
      if (decl.typeParams.length === 0) {
        return { e: "construct", typeName: name, valueType: this.valueTypes.has(name), fields: this.fieldInits(name, expr, env), type: this.namedType(name) };
      }
      // A generic construct's type arguments are what the checker inferred and
      // recorded (an explicit `<…>` overrides; a variable is lowered under the current
      // instantiation). Reading them removes the backend's own field-value inference —
      // the checker already resolved `Box`'s `T` from the value, distinct from any
      // enclosing impl's `T`.
      const recorded = this.elab.typeArgs.get(expr);
      const typeArgs = decl.typeParams.map((tp, i) =>
        expr.typeArgs[i] ? this.resolveType(expr.typeArgs[i]!) : recorded?.[i] ? this.lowerType(recorded[i]!) : UNKNOWN
      );
      // Lower each field value with the struct's type parameters bound to this
      // construction's arguments, so an otherwise-ambiguous payload resolves its *own*
      // recorded type to the concrete instance — a bare `None()` in `Future<T>(value:
      // None())` recorded `Option<T>` becomes `Option__int`. No per-field expected type
      // is pushed; the payload reads the elaboration and substitutes under the bindings.
      const paramBindings = new Map(this.typeBindings);
      decl.typeParams.forEach((tp, i) => paramBindings.set(tp.name, typeArgs[i]!));
      const fields = this.withBindings(paramBindings, () => this.fieldInits(name, expr, env));
      const typeName = this.instantiateStruct(name, typeArgs);
      return { e: "construct", typeName, valueType: false, fields, type: { k: "struct", name: typeName } };
    }
    if (this.variantOf.has(name)) {
      const { enumName, variant } = this.variantOf.get(name)!;
      const decl = this.enums.get(enumName)!;
      if (decl.typeParams.length === 0) {
        const fields = this.variantInits(variant, expr, env);
        return { e: "enumConstruct", enumName, variant: variant.name, fields, type: { k: "enum", name: enumName } };
      }
      // A generic construct flattens to its instantiation, like a struct — but a
      // payload-free variant (`None()`) carries no value to infer the arguments
      // from, so it falls back to the `expected` type (the enclosing `return` /
      // binding target the checker already resolved).
      const typeArgs = this.enumConstructArgs(enumName, decl, expr, expected);
      // Lower each payload with the enum's parameters bound to this construction's
      // arguments, so a nested payload (`None()` in `Ok(ok: None())`) resolves its own
      // recorded type to the concrete instance — the enum analogue of the struct case.
      const paramBindings = new Map(this.typeBindings);
      decl.typeParams.forEach((tp, i) => paramBindings.set(tp.name, typeArgs[i]!));
      const fields = this.withBindings(paramBindings, () => this.variantInits(variant, expr, env));
      const instName = this.instantiateEnum(enumName, typeArgs);
      return { e: "enumConstruct", enumName: instName, variant: variant.name, fields, type: { k: "enum", name: instName } };
    }
    if (ast.isGenericContainer(name)) {
      // The element type is named at construction (`Set<int>()`, `Dict<K, V>()`),
      // so resolve it from the explicit type arguments — which pass through
      // `resolveType`, honoring the *current instantiation's* type bindings. That
      // matters inside a generic prelude/user function: `Set<T>()` in `awaitAll<T>`
      // must become `Set<int>` for a concrete call, not the `Set<T>` the checker
      // recorded when it type-checked the body generically. Only when a type
      // argument is somehow absent do we fall back to the checker's node type.
      const inferred = this.elab.types.get(expr);
      const infArgs = inferred && inferred.k === "generic" && inferred.name === name ? inferred.args : [];
      const argAt = (i: number): Type => (expr.typeArgs[i] ? this.resolveType(expr.typeArgs[i]!) : infArgs[i] ?? UNKNOWN);
      const type: Type =
        name === "Dict" ? { k: "generic", name, args: [argAt(0), argAt(1)] } : { k: "generic", name, args: [argAt(0)] };
      const gargs = (type as Type & { k: "generic" }).args;
      const elem = gargs[0] ?? UNKNOWN;
      // Install the container's cursor-maker on the new collection, so it can later
      // be iterated as an `Iterable` (see `collRec.iterFn`).
      const iterFn = this.containerAdapter(name as "Set" | "Dict" | "List", gargs).iterFn;
      return { e: "collectionNew", container: name, elem, type, iterFn };
    }
    const fn = this.fns.get(name);
    if (fn) {
      // Pass a lambda argument its parameter's type as the expected type — this is
      // what pins the lambda's own parameters (`apply((x) -> …, 6)`). Only resolved
      // for a lambda arg: for a generic fn, resolving `Pair<A, B>` with the type
      // variables still unbound would instantiate a spurious `Pair<unknown, …>`.
      const loweredArgs = expr.args.map((a, i) =>
        this.lowerExpr(a.value, env, a.value.kind === "lambda" && fn.params[i] ? this.resolveType(fn.params[i]!.type) : undefined)
      );
      if (fn.typeParams.length > 0) {
        // A generic call: infer the type arguments, then dispatch to the (memoized)
        // instantiation for that concrete tuple — this is the monomorphization.
        const inst = this.instantiateFn(fn, expr);
        return { e: "staticCall", target: inst.id, args: loweredArgs, type: inst.ret };
      }
      return { e: "staticCall", target: name, args: loweredArgs, type: fn.returnType ? this.resolveType(fn.returnType) : VOID };
    }
    if (BUILTINS[name]) {
      const decl = this.builtinDecls.get(name);
      // A *generic* builtin (`race<T>(Set<Future<T>>) T`): infer its type parameters
      // from the (concrete, already-lowered) argument types and substitute them into
      // the return type — the same inference `instantiateFn` does for a user generic,
      // but with no body to emit. This keeps `T` from leaking to the backend as an
      // unbound var.
      if (decl && decl.typeParams.length > 0) {
        const loweredArgs = args();
        return { e: "builtin", name, args: loweredArgs, type: this.genericBuiltinReturn(decl, expr) };
      }
      // Resolve the builtin's *declared* return type here (rather than reusing the
      // checker's `BUILTINS.ret`) so a file-IO builtin's `Res<IOError, …>` is
      // monomorphized to a concrete enum instance — created and emitted — that the
      // backend then constructs.
      const type = decl?.returnType ? this.resolveType(decl.returnType) : BUILTINS[name]!.ret;
      return { e: "builtin", name, args: args(), type };
    }
    // Calling a function *value* — a local (parameter, let-bound closure) of fn type.
    const localType = env.lookup(name);
    if (localType?.k === "fn") {
      const callee: CoreExpr = { e: "local", name, type: localType };
      const callArgs = expr.args.map((a, i) => this.lowerExpr(a.value, env, localType.params[i]));
      return { e: "callValue", callee, args: callArgs, type: localType.ret };
    }
    throw new MonoError(`unknown function or type \`${name}\``);
  }

  /** The i-th explicit type argument of a call, resolved; `unknown` if absent. */
  private argType(expr: ast.CallExpr, i: number): Type {
    return expr.typeArgs[i] ? this.resolveType(expr.typeArgs[i]!) : UNKNOWN;
  }

  private lowerMethodCall(expr: ast.CallExpr, callee: ast.FieldAccess, env: Env): CoreExpr {
    const recv = this.lowerExpr(callee.object, env);
    const method = callee.field;
    const t = recv.type;
    // A call through a function-typed *field* (`p.step(10)`): read the field, then
    // call the value. (A field wins over a method of the same name — it's data.)
    if (t.k === "struct") {
      const ft = this.fieldTypeOrNull(t.name, method);
      if (ft?.k === "fn") {
        const fieldExpr: CoreExpr = { e: "field", object: recv, field: method, type: ft };
        const callArgs = expr.args.map((a, i) => this.lowerExpr(a.value, env, ft.params[i]));
        return { e: "callValue", callee: fieldExpr, args: callArgs, type: ft.ret };
      }
      // A generic method (`fn map<U>(…)`) is monomorphized per call, like a generic
      // fn — and so is a default inherited from a generic trait (`filter`), whose body
      // is generic over the trait's parameters: `instantiateMethod` binds them and pins
      // its lambda arguments' types (the eager sweep skips both — see `run`).
      const m = this.methodImpls.get(t.name)?.get(method);
      if (m && (m.typeParams.length > 0 || this.isDeferredDefault(m))) return this.instantiateMethod(t.name, method, m, expr, recv, env);
    }
    if (t.k === "generic" && (t.name === "Set" || t.name === "Dict" || t.name === "List")) {
      // `container.iter()` — the `Iterable` builtin — builds the collection's concrete
      // per-element cursor (`SetIter<int>`/`DictIter<K, V>`/`ListIter<T>`) via a static
      // call to its `iter` (the same one installed on the collection at construction).
      // The receiver stays the erased collection; only the cursor it produces is typed.
      if (method === "iter") {
        const a = this.containerAdapter(t.name, t.args);
        return { e: "staticCall", target: a.iterFn as FuncId, args: [recv], type: { k: "struct", name: a.struct } };
      }
      // A container's `Iterable` defaults (`map`/`filter`/`reduce`) — and any generic
      // user impl method — are monomorphized per call, like a struct's: the receiver's
      // element type binds the trait/impl parameters (see instantiateMethod). `reduce`
      // scans natively via `for x in self`; `map`/`filter` build a lazy view whose
      // `source` is the collection itself, iterated through the `Iterable` dispatch.
      const m = this.methodImpls.get(t.name)?.get(method);
      if (m && !m.builtin && (m.typeParams.length > 0 || this.isDeferredDefault(m)))
        return this.instantiateMethod(t.name, method, m, expr, recv, env);
    }
    const args = expr.args.map((a) => this.lowerExpr(a.value, env));
    if (t.k === "generic" && (t.name === "Set" || t.name === "Dict" || t.name === "List")) {
      // A derived container method (`set.union(other)`) written in STM: monomorphize
      // it per element type on demand — like a generic function, but keyed off the
      // receiver's element args (`Set<int>.union` ⇒ `Set_union__int`). Lowering under
      // those bindings, with the concrete `self` type, means `Set<T>()` inside the
      // body builds a `Set<int>` (installing the *int* cursor its result must carry so
      // a later `map`/`filter` over it iterates as `int`), and `for x in self` binds
      // `int`. Guard before lowering so a self-referential body can't recurse forever.
      const derived = this.containerDerived.get(t.name)?.get(method);
      if (derived) {
        const bindings = this.containerReceiverBindings(t) ?? new Map<string, Type>();
        const id: FuncId = `${t.name}_${method}__${t.args.map(mangleType).join("_")}`;
        if (!this.emitted.has(id)) {
          this.emitted.add(id);
          this.funcs.push(
            this.withBindings(bindings, () => this.lowerFunc(id, t.name, derived.params, derived.returnType, derived.body, t))
          );
        }
        const type = derived.returnType ? this.withBindings(bindings, () => this.resolveType(derived.returnType!, t.name)) : VOID;
        return { e: "staticCall", target: id, args: [recv, ...args], type };
      }
      // A concrete user container-impl method (`impl<T> Countable for Set<T>`) → a
      // static call to `Set_count`, like a struct method. The built-in flat ops
      // (add/get/size/…) aren't in methodImpls — they're `builtinMethod` primitives.
      const m = this.methodImpls.get(t.name)?.get(method);
      if (m && !m.builtin) return { e: "staticCall", target: `${t.name}_${method}`, args: [recv, ...args], type: this.callResultType(expr) };
      return { e: "builtinMethod", method, recv, args, type: this.callResultType(expr) };
    }
    if (t.k === "struct" || t.k === "enum") {
      // A `builtin` method (e.g. `conn.send(data)`) has no body: lower it to a
      // `builtin` Core node, exactly like a builtin free function, so the backend
      // plugs it in natively. The name is `Type_method` (mirroring the staticCall
      // target), which keeps two types' same-named builtins (`TcpConn.close` vs
      // `TcpListener.close`) distinct. `self` leads the args, as for any method.
      const m = this.methodImpls.get(t.name)?.get(method);
      if (m?.builtin) {
        return { e: "builtin", name: `${t.name}_${method}`, args: [recv, ...args], type: this.callResultType(expr) };
      }
      // Concrete receiver -> static dispatch (this is where a specialized default
      // body's `self.m()` correctly resolves to `Type_m`, not a dynamic call).
      return { e: "staticCall", target: `${t.name}_${method}`, args: [recv, ...args], type: this.callResultType(expr) };
    }
    const primKey = primitiveImplKey(t);
    if (primKey) {
      // A method on a primitive (`s.charAt(i)`, `c.isDigit()`, `n.cmp(m)`), from a
      // prelude impl on the type — the inherent `impl str`/`impl char`, or an
      // `impl Ord for int`/`float`/`bool`/… Keyed under the impl name (`string` ->
      // `str`). A `builtin` method has no body — lower to a `builtin` Core node the
      // backend plugs in natively; a bodied one is a static call to the
      // monomorphized method (`str_split`, `int_cmp`), with `self` leading.
      const key = primKey;
      const m = this.methodImpls.get(key)?.get(method);
      if (!m) throw new MonoError(`\`${key}\` has no method \`${method}\``);
      if (m.builtin) {
        return { e: "builtin", name: `${key}_${method}`, args: [recv, ...args], type: this.callResultType(expr) };
      }
      // A bodied method (`str.split`, `char.isDigit`): lower it on demand and call
      // it statically. Mark it emitted *before* lowering the body, so a method that
      // (transitively) calls itself can't recurse forever.
      const id: FuncId = `${key}_${method}`;
      if (!this.emitted.has(id)) {
        this.emitted.add(id);
        this.funcs.push(this.lowerFunc(id, key, m.params, m.returnType, m.body));
      }
      return { e: "staticCall", target: id, args: [recv, ...args], type: this.callResultType(expr) };
    }
    if (t.k === "trait") {
      const sig = this.traits.get(t.name)?.methods.find((m) => m.name === method);
      this.dynDispatched.add(`${t.name}#${method}`); // build a dispatch table for it (see run)
      // Bind the trait's type parameters to this instantiation's arguments so the
      // result type is concrete at the call site — `Iterator<int>.next()` is
      // `Option<int>`, i.e. the monomorphized `Option__int`. The shared dispatch
      // function can't have that single type (each impl differs), so it returns
      // `any` and the backend asserts the result back here (see `dispatchTable`).
      // A `Self` return stays the trait type (`any`), as before.
      const traitDecl = this.traits.get(t.name);
      const bindings = new Map<string, Type>();
      traitDecl?.typeParams.forEach((tp, i) => bindings.set(tp.name, t.args[i] ?? UNKNOWN));
      const retTe = sig?.returnType;
      const type = retTe ? this.withBindings(bindings, () => this.traitMethodType(retTe, t.name)) : VOID;
      return { e: "dynCall", trait: t.name, method, recv, args, type };
    }
    throw new MonoError(`method \`${method}\` on non-dispatchable type (${t.k})`);
  }

  // -- helpers ------------------------------------------------------------

  private fieldInits(structName: string, call: ast.CallExpr, env: Env): CoreFieldInit[] {
    const decl = this.structs.get(structName)!;
    const provided = new Map<string, CoreExpr>();
    // Each field value is lowered on its own — a nested construction reads the type
    // arguments the checker recorded on it, and a lambda reads its recorded (now
    // concrete) function type — so no field type is pushed down as its expected type.
    // A generic construction lowers these under the struct's parameter bindings (see
    // the caller), which is what resolves a payload's recorded type variable.
    call.args.forEach((arg, i) => {
      const field = decl.fields.find((f) => f.name === (arg.name ?? decl.fields[i]?.name));
      if (field) provided.set(field.name, this.lowerExpr(arg.value, env));
    });
    return decl.fields
      .filter((f) => provided.has(f.name))
      .map((f) => ({ name: f.name, value: provided.get(f.name)! }));
  }

  private variantInits(variant: ast.EnumVariant, call: ast.CallExpr, env: Env): CoreFieldInit[] {
    const provided = new Map<string, CoreExpr>();
    call.args.forEach((arg, i) => {
      const fieldName = arg.name ?? variant.fields[i]?.name;
      if (fieldName) provided.set(fieldName, this.lowerExpr(arg.value, env));
    });
    return variant.fields.filter((f) => provided.has(f.name)).map((f) => ({ name: f.name, value: provided.get(f.name)! }));
  }

  /** The concrete type arguments for a generic enum construction: an explicit
   *  `Some<int>(…)`; else the checker's recorded arguments (lowered), which cover
   *  both `Some(value: 5)` (pinned from the payload) and a bare `None()` (pinned from
   *  context); else the `expected` enum instance's arguments. */
  private enumConstructArgs(enumName: string, decl: ast.EnumDecl, expr: ast.CallExpr, expected: Type | undefined): Type[] {
    // The checker resolved this construction's type arguments — including from
    // context, so a bare `None()` is typed `Option<int>` by the enclosing
    // `orElse`/return — and recorded them. Consume that decision (an explicit `<…>`
    // overrides; a variable is lowered under the current instantiation via
    // `lowerType`). Only when it left a parameter unresolved do we fall back to the
    // `expected` enum instance's arguments.
    const info = expected?.k === "enum" ? this.instanceInfo.get(expected.name) : undefined;
    const expectedArgs = info?.head === enumName ? info.args : [];
    const recorded = this.elab.typeArgs.get(expr);
    return decl.typeParams.map((tp, i) => {
      if (expr.typeArgs[i]) return this.resolveType(expr.typeArgs[i]!);
      if (recorded?.[i]) {
        const lowered = this.lowerType(recorded[i]!);
        if (lowered.k !== "unknown") return lowered;
      }
      return expectedArgs[i] ?? UNKNOWN;
    });
  }

  private fieldType(objType: Type, field: string, span: ast.Span): Type {
    if (objType.k !== "struct") throw new MonoError(`field access on non-struct (${objType.k})`);
    // A generic struct instance's fields are already concrete (types substituted).
    const instance = this.structInstances.get(objType.name);
    if (instance) {
      const f = instance.fields.find((f) => f.name === field);
      if (!f) throw new MonoError(`no field \`${field}\` on \`${objType.name}\``);
      return f.type;
    }
    const decl = this.structs.get(objType.name);
    if (decl && decl.typeParams.length > 0) throw new MonoError(`generic struct field access is not lowered yet`);
    const fd = decl?.fields.find((f) => f.name === field);
    if (!fd) throw new MonoError(`no field \`${field}\` on \`${objType.name}\``);
    return this.resolveType(fd.type);
  }

  /** Materialize a generic struct for one concrete type-argument tuple: a Core
   *  struct with the fields' types substituted, plus specialized copies of every
   *  impl method for that instance. Memoized; recursion-safe (reserved before its
   *  fields are filled). Returns the instance's mangled struct name. */
  private instantiateStruct(headName: string, typeArgs: Type[]): string {
    const mangled = `${headName}__${typeArgs.map(mangleType).join("_")}`;
    if (this.structInstances.has(mangled)) return mangled;
    const decl = this.structs.get(headName)!;
    const bindings = new Map(decl.typeParams.map((tp, i) => [tp.name, typeArgs[i] ?? UNKNOWN] as const));
    const prev = this.typeBindings;
    this.typeBindings = bindings;
    try {
      const struct: CoreStruct = { name: mangled, valueType: false, fields: [] };
      this.structInstances.set(mangled, struct); // reserve before filling (recursion)
      this.instanceInfo.set(mangled, { head: headName, args: typeArgs });
      struct.fields = decl.fields.map((f) => ({ name: f.name, type: this.resolveType(f.type), key: f.key === true }));
      for (const impl of this.genericImpls.get(headName) ?? []) this.instantiateImpl(impl, mangled);
      return mangled;
    } finally {
      this.typeBindings = prev;
    }
  }

  /** Instantiate a generic enum for a concrete type-argument tuple, materializing
   *  a mangled instance (`Res__IOError_string`) with every variant payload resolved
   *  to a concrete type — the enum analogue of `instantiateStruct`. Returns the
   *  mangled name; `resolveType` and `enumConstruct` flatten a generic enum to it,
   *  so the rest of the pipeline treats it as an ordinary (kind `enum`) type. */
  private instantiateEnum(headName: string, typeArgs: Type[]): string {
    const mangled = `${headName}__${typeArgs.map(mangleType).join("_")}`;
    if (this.enumInstances.has(mangled)) return mangled;
    const decl = this.enums.get(headName)!;
    const bindings = new Map(decl.typeParams.map((tp, i) => [tp.name, typeArgs[i] ?? UNKNOWN] as const));
    return this.withBindings(bindings, () => {
      const inst: CoreEnum = { name: mangled, variants: [] };
      this.enumInstances.set(mangled, inst); // reserve before filling (recursion)
      this.instanceInfo.set(mangled, { head: headName, args: typeArgs }); // recover args (see enumConstructArgs)
      inst.variants = decl.variants.map((v) => ({
        name: v.name,
        fields: v.fields.map((f) => ({ name: f.name, type: this.resolveType(f.type), key: f.key === true })),
      }));
      // Specialize the enum's generic impls for this instance, exactly as
      // `instantiateStruct` does — this is what gives `Option__int` its
      // `Option__int_getOr`, so a method on a generic enum (`impl<T> Option<T> { … }`)
      // reaches the compiled backends and not just the interpreter.
      for (const impl of this.genericImpls.get(headName) ?? []) this.instantiateImpl(impl, mangled);
      return mangled;
    });
  }

  /** Specialize one generic impl's methods for a concrete instance of its target —
   *  a struct (`Box__int`) or an enum (`Option__int`) — the same monomorphization the
   *  non-generic method sweep does, but with the target's type parameters bound
   *  (which `this.typeBindings` already carries here). */
  private instantiateImpl(decl: ast.ImplDecl, instanceName: string) {
    const inherited = { trait: decl.trait, traitArgs: decl.traitArgs };
    const overridden = new Set(decl.methods.map((m) => m.name));
    const methods: Method[] = decl.methods.map((m) => this.asMethod(m));
    for (const m of this.traits.get(decl.trait)?.methods ?? []) {
      if (m.body && !overridden.has(m.name)) methods.push(this.asMethod(m, inherited));
    }
    let table = this.methodImpls.get(instanceName);
    if (!table) this.methodImpls.set(instanceName, (table = new Map()));
    for (const m of methods) {
      table.set(m.name, m); // registered for lookup; a generic method or a default
      // inherited from a generic trait is resolved per call by instantiateMethod (its
      // trait parameters bind there, not from this instance's struct bindings — which
      // may even name a colliding `T`), so only lower the eager ones here.
      if (m.builtin) continue; // a `builtin` method has no body to lower (native)
      if (m.typeParams.length > 0 || this.isDeferredDefault(m)) continue;
      this.pushFunc(this.lowerFunc(`${instanceName}_${m.name}`, instanceName, m.params, m.returnType, m.body));
    }
    const impls = this.traitImpls.get(decl.trait) ?? [];
    if (!impls.includes(instanceName)) impls.push(instanceName);
    this.traitImpls.set(decl.trait, impls);
    // Record the instance's loop-element type (its `Iterable<E>`/`Iterator<E>` arg,
    // resolved under the target's bindings) so `for x in <instance>` types `x`.
    if ((decl.trait === "Iterable" || decl.trait === "Iterator") && decl.traitArgs[0]) {
      this.iterElem.set(instanceName, this.resolveType(decl.traitArgs[0]));
    }
  }

  /** The result type of a lowered method call: the type the checker recorded on the
   *  call node, lowered through the current instantiation's bindings (`Option<T>` →
   *  `Option__int`). The checker already typed the node with the receiver's type
   *  arguments substituted, so the monomorphizer just substitutes-and-mangles it
   *  rather than re-resolving the method's declared return type. */
  private callResultType(expr: ast.CallExpr): Type {
    return this.lowerType(this.elab.types.get(expr) ?? UNKNOWN);
  }

  private implementsTrait(typeName: string, trait: string): boolean {
    return this.traitImpls.get(trait)?.includes(typeName) ?? false;
  }

  /** Whether a declared type expression references any of `names` (a trait's type
   *  parameters and `Self`) — the test for whether a dispatch return is specialized
   *  per impl and so must be erased at the shared stub. */
  private typeExprMentions(te: ast.TypeExpr, names: Set<string>): boolean {
    if (te.kind === "fn") return te.params.some((p) => this.typeExprMentions(p, names)) || this.typeExprMentions(te.ret, names);
    return names.has(te.name) || te.args.some((a) => this.typeExprMentions(a, names));
  }

  private namedType(name: string): Type {
    // A container impl target (`impl<T> Trait for Set<T>`) — `self` is the generic
    // collection, so `self.size()` etc. resolve as collection methods. The element
    // args are opaque here; a *specialized* method receives its concrete `self` type
    // (`Set<int>`) directly (see `lowerFunc`), so `for x in self` binds the element.
    if (name === "Set") return { k: "generic", name, args: [UNKNOWN] };
    if (name === "List") return { k: "generic", name, args: [UNKNOWN] };
    if (name === "Dict") return { k: "generic", name, args: [UNKNOWN, UNKNOWN] };
    // Primitive self, for a method in an impl on a scalar (`impl str`/`impl char`,
    // or `impl Ord for int`/`float`/`bool`): `self` is the scalar itself, so
    // `self.slice(...)` / `self < other` resolve — and the backend emits the right
    // scalar receiver type (`int_cmp(self int64, …)`, not a `*Object`).
    if (name === "string" || name === "str") return STRING;
    if (name === "char") return CHAR;
    if (name === "int") return INT;
    if (name === "float") return FLOAT;
    if (name === "bool") return BOOL;
    // A head enum (`Option`) or one of its instances (`Option__int`, materialized by
    // `instantiateEnum`) — both are enum-kinded, so a method specialized onto an
    // instance takes `self` as that enum rather than a struct pointer.
    if (this.enums.has(name) || this.enumInstances.has(name)) return { k: "enum", name };
    return { k: "struct", name }; // structs and value types share the `struct` kind
  }

  /** Lower a checker `Type` to its concrete Core type under the current type
   *  bindings — the `Type`-to-`Type` analogue of `resolveType` (which lowers a
   *  `TypeExpr`). This is how the monomorphizer *consumes* a type the checker
   *  recorded (in the elaboration) instead of re-deriving it: substitute the type
   *  variables in scope, then flatten each generic to its instantiation
   *  (`Box<int>` -> `Box__int`, `Option<T>` under `T=int` -> `Option__int`). A
   *  scalar or already-concrete type passes through; an unbound variable — one the
   *  current instantiation doesn't fix — becomes `unknown`, matching `resolveType`. */
  private lowerType(t: Type): Type {
    switch (t.k) {
      case "int": case "float": case "bool": case "string": case "char": case "void": case "unknown":
      case "struct": case "enum": // non-generic: already a concrete instance
        return t;
      case "var":
        return this.typeBindings.get(t.name) ?? UNKNOWN;
      case "fn":
        return { k: "fn", params: t.params.map((p) => this.lowerType(p)), ret: this.lowerType(t.ret) };
      case "struct-generic":
        return { k: "struct", name: this.instantiateStruct(t.name, t.args.map((a) => this.lowerType(a))) };
      case "enum-generic":
        return { k: "enum", name: this.instantiateEnum(t.name, t.args.map((a) => this.lowerType(a))) };
      case "generic": // a built-in container (Set/Dict/List)
        return { k: "generic", name: t.name, args: t.args.map((a) => this.lowerType(a)) };
      case "trait":
        return { k: "trait", name: t.name, args: t.args.map((a) => this.lowerType(a)) };
      default:
        return UNKNOWN;
    }
  }

  /** Resolve a source type expression to a concrete `Type`, substituting `Self`
   *  with `selfName` (or the current specialization's `self`). */
  private resolveType(t: ast.TypeExpr, selfName?: string): Type {
    if (t.kind === "fn") {
      return { k: "fn", params: t.params.map((p) => this.resolveType(p, selfName)), ret: this.resolveType(t.ret, selfName) };
    }
    const name = t.name;
    if (name === "Self") {
      const s = selfName ? this.namedType(selfName) : this.self;
      if (!s) throw new MonoError("`Self` outside a method");
      return s;
    }
    // A generic type parameter in scope resolves to its concrete binding.
    const bound = this.typeBindings.get(name);
    if (bound) return bound;
    switch (name) {
      case "int": return INT;
      case "float": return FLOAT;
      case "bool": return BOOL;
      case "string":
      case "str": return STRING;
      case "char": return CHAR;
      case "void": return VOID;
      case "Set": return { k: "generic", name, args: [t.args[0] ? this.resolveType(t.args[0], selfName) : UNKNOWN] };
      case "List": return { k: "generic", name, args: [t.args[0] ? this.resolveType(t.args[0], selfName) : UNKNOWN] };
      case "Dict":
        return { k: "generic", name, args: [t.args[0] ? this.resolveType(t.args[0], selfName) : UNKNOWN, t.args[1] ? this.resolveType(t.args[1], selfName) : UNKNOWN] };
    }
    if (this.structs.has(name)) {
      const decl = this.structs.get(name)!;
      if (decl.typeParams.length === 0) return { k: "struct", name };
      // A concrete generic struct type (`Box<int>`) flattens to its instantiation.
      const typeArgs = decl.typeParams.map((tp, i) => (t.args[i] ? this.resolveType(t.args[i], selfName) : UNKNOWN));
      return { k: "struct", name: this.instantiateStruct(name, typeArgs) };
    }
    if (this.enums.has(name)) {
      const decl = this.enums.get(name)!;
      if (decl.typeParams.length === 0) return { k: "enum", name };
      // A concrete generic enum type (`Res<IOError, string>`) flattens to its
      // instantiation, just like a generic struct — so it is emitted with concrete
      // payloads and a `match` needs no boxing.
      const typeArgs = decl.typeParams.map((tp, i) => (t.args[i] ? this.resolveType(t.args[i], selfName) : UNKNOWN));
      return { k: "enum", name: this.instantiateEnum(name, typeArgs) };
    }
    if (this.traits.has(name)) return { k: "trait", name, args: t.args.map((a) => this.resolveType(a, selfName)) };
    return UNKNOWN;
  }
}

/** The first candidate type that actually carries information (isn't `unknown`) —
 *  used to prefer a real type over a still-unbound type variable when typing a lambda. */
function concrete(...candidates: (Type | undefined)[]): Type | undefined {
  return candidates.find((t) => t !== undefined && t.k !== "unknown");
}

/** A concrete type as a Go-safe identifier fragment, for a generic instantiation's
 *  mangled name (`Set<int>` -> `Set_int`), so `bigger<Set<int>>` -> `bigger__Set_int`. */
export function mangleType(t: Type): string {
  switch (t.k) {
    case "int": case "float": case "bool": case "string": case "char": case "void": case "unknown":
      return t.k;
    case "struct": case "enum": case "var":
      return t.name;
    case "struct-generic": case "enum-generic": case "generic": case "trait":
      return t.args.length ? `${t.name}_${t.args.map(mangleType).join("_")}` : t.name;
    default:
      return "x";
  }
}
